import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import "./View.css";
import "../../../../shared-ui/crane-tokens.css";
import { AccessDenied } from "../../../../shared-ui/States";
import "../../../../shared-ui/stitch-forms.css";
import { useParams, NavLink } from "react-router-dom";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { WorkOrderDocument } from "./Print";

const usePermissions = () => {
  const authData = useMemo(() => {
    try {
      const userDetailsString = localStorage.getItem("zTaskUserDetails");
      if (!userDetailsString) return { user: null, permissions: [] };
      const userDetails = JSON.parse(userDetailsString);
      const permissions = userDetails?.role?.permissions
        ? userDetails.role.permissions.map((p) => p.title)
        : [];
      return { user: userDetails, permissions };
    } catch (error) {
      console.error("Failed to parse user details from localStorage:", error);
      return { user: null, permissions: [] };
    }
  }, []);

  const hasPermission = useCallback(
    (requiredPermission) => {
      if (Array.isArray(requiredPermission)) {
        return requiredPermission.some((p) => authData.permissions.includes(p));
      }
      return authData.permissions.includes(requiredPermission);
    },
    [authData]
  );

  return { user: authData.user, hasPermission };
};

function WorkOrderView() {
  const { id } = useParams();
  const ref = useRef();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [company, setCompany] = useState(null);
  const [allData, setAllData] = useState(null);
  const token = localStorage.getItem("zTaskAuthToken");
  const { user, hasPermission } = usePermissions();

  useEffect(() => {
    setIsLoading(true);
    if (
      !hasPermission("VIEW WORK ORDER") &&
      !hasPermission("VIEW ALL WORK ORDERS")
    ) {
      setError(
        "Access Denied: You do not have permission to view work orders."
      );
      return;
    }
    const fetchCompany = async () => {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/get-company`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.ok) {
        const data = await res.json();
        setCompany(data.message[0]);
      }
    };
    const fetchWorkOrder = async () => {
      await fetchCompany();
      const res = await fetch(
        `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/work-order/api/work-orders/${id}/report`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.ok) {
        const data = await res.json();
        setAllData(data);
      }
    };
    fetchWorkOrder().finally(() => {
      setIsLoading(false);
    });
  }, [id, token, hasPermission, user]);

  const validTransactions = useMemo(() => {
    if (!allData?.work_order_tran) return [];
    return allData.work_order_tran.filter(
      tran => tran !== null && tran !== undefined && typeof tran === 'object'
    );
  }, [allData]);

  // Calculate grand total - simply sum all transaction totals (tax already included)
  const grandTotal = useMemo(() => {
    if (!validTransactions.length) return 0;
    
    return validTransactions.reduce((sum, tran) => {
      return sum + parseFloat(tran.total_amount || 0);
    }, 0);
  }, [validTransactions]);

  const formatCurrency = (value) => {
    return parseFloat(value || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  if (isLoading)
    return <div className="loading-message">Loading work order details...</div>;
  if (error) {
    if (error.startsWith("Access Denied")) {
      return (
        <AccessDenied description="You do not have permission to view work orders." />
      );
    }
    return <div className="error-message">{error}</div>;
  }
  if (!allData || !company)
    return (
      <div className="error-message">
        Error: Work Order data could not be fully loaded. Please try again.
      </div>
    );

  const woNo = allData?.work_order_master?.wo_no;

  return (
    <div className="work-order-view-page stitch-wo-document">
      <header className="page-header">
        <div className="download-button-div">
          <NavLink to="/work-orders">
            <button type="button" className="backbutton">Back To List</button>
          </NavLink>
          {hasPermission("DOWNLOAD WORK ORDER PDF") && (
            <PDFDownloadLink
              document={
                <WorkOrderDocument allData={allData} company={company} />
              }
              fileName={`work-order-${woNo || id}.pdf`}
              className="download-button"
            >
              {({ loading }) =>
                loading ? "Loading document..." : "Export PDF"
              }
            </PDFDownloadLink>
          )}
          <NavLink to={`/work-orders/${id}/print`} className="download-button" style={{ background: "var(--crane-surface-low,#eff4ff)", color: "var(--crane-on-surface,#0b1c30)", border: "1px solid var(--crane-border,#e2e8f0)" }}>
            Print Work Order
          </NavLink>
        </div>
      </header>

      <div className="work-order-container" ref={ref}>
        {/* Header Section */}
        <div className="header-section">
          <div className="company-info">
            <div className="company-name">{company.companyName}</div>
            <div className="company-details">
              {(company.address || "").trim() ? (
                <div className="company-detail-row">
                  <span className="company-detail-label">Address</span>
                  <span className="company-detail-value">{company.address}</span>
                </div>
              ) : null}
              {(company.telephoneNumber || "").trim() ? (
                <div className="company-detail-row">
                  <span className="company-detail-label">Phone</span>
                  <span className="company-detail-value">{company.telephoneNumber}</span>
                </div>
              ) : null}
              {(company.websiteUrl || "").trim() ? (
                <div className="company-detail-row">
                  <span className="company-detail-label">Website</span>
                  <span className="company-detail-value">{company.websiteUrl}</span>
                </div>
              ) : null}
            </div>
          </div>
          <div className="work-order-info">
            <div className="work-order-title">WORK ORDER</div>
            <table className="wo-meta-table">
              <tbody>
                <tr>
                  <th scope="row">Work Order #</th>
                  <td>{allData.work_order_master.wo_no || "—"}</td>
                </tr>
                <tr>
                  <th scope="row">Date</th>
                  <td>
                    {allData.work_order_master.wo_date
                      ? new Date(allData.work_order_master.wo_date).toLocaleDateString()
                      : "—"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Customer + Project — single aligned 2-column panel */}
        <div className="parties-section">
          <div className="party-panel">
            <div className="party-panel__header">Customer details</div>
            <div className="party-panel__body">
              <div className="party-row">
                <span className="party-label">Name</span>
                <span className="party-value">{allData.customers?.name || "—"}</span>
              </div>
              <div className="party-row">
                <span className="party-label">Address</span>
                <span className="party-value">{allData.customers?.address || "—"}</span>
              </div>
              <div className="party-row">
                <span className="party-label">City</span>
                <span className="party-value">
                  {[allData.customers?.city, allData.customers?.state_name].filter(Boolean).join(", ") || "—"}
                </span>
              </div>
              <div className="party-row">
                <span className="party-label">Pincode</span>
                <span className="party-value">{allData.customers?.pincode || "—"}</span>
              </div>
              <div className="party-row">
                <span className="party-label">Vendor code</span>
                <span className="party-value">{allData.customers?.vendor_code || "—"}</span>
              </div>
              <div className="party-row">
                <span className="party-label">Contact</span>
                <span className="party-value">{allData.customers?.contact_details || "—"}</span>
              </div>
              <div className="party-row">
                <span className="party-label">GST No</span>
                <span className="party-value">{allData.customers?.gst_no || "—"}</span>
              </div>
              <div className="party-row">
                <span className="party-label">PAN No</span>
                <span className="party-value">{allData.customers?.pan_no || "—"}</span>
              </div>
            </div>
          </div>
          <div className="party-panel">
            <div className="party-panel__header">Project details</div>
            <div className="party-panel__body">
              <div className="party-row">
                <span className="party-label">Name</span>
                <span className="party-value">{allData.projects?.project_name || "—"}</span>
              </div>
              <div className="party-row">
                <span className="party-label">Address</span>
                <span className="party-value">{allData.projects?.address || "—"}</span>
              </div>
              <div className="party-row">
                <span className="party-label">City</span>
                <span className="party-value">
                  {[allData.projects?.city, allData.projects?.state].filter(Boolean).join(", ") || "—"}
                </span>
              </div>
              <div className="party-row">
                <span className="party-label">Pincode</span>
                <span className="party-value">{allData.projects?.pin_code || "—"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Summary and Details Section */}
        <div className="section-grid">
          <div className="comments-section">
            <div className="comments-title">Other details</div>
            <div className="field-row">
              <span className="field-label">WO Start</span>
              <span className={`field-value ${!allData.work_order_master.wo_start && "empty"}`}>
                {allData.work_order_master.wo_start
                  ? new Date(allData.work_order_master.wo_start).toLocaleDateString()
                  : "Not specified"}
              </span>
            </div>
            <div className="field-row">
              <span className="field-label">WO End</span>
              <span className={`field-value ${!allData.work_order_master.wo_end && "empty"}`}>
                {allData.work_order_master.wo_end
                  ? new Date(allData.work_order_master.wo_end).toLocaleDateString()
                  : "Not specified"}
              </span>
            </div>
            <div className="field-row">
              <span className="field-label">No. of Shifts</span>
              <span className="field-value">{allData.work_order_master.no_shifts || "1"}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Shift Hours</span>
              <span className="field-value">{allData.work_order_master.shift_hours || "12"} hrs</span>
            </div>
            <div className="field-row">
              <span className="field-label">Monthly Hrs</span>
              <span className="field-value">{allData.work_order_master.monthly_hrs || "360"} hrs</span>
            </div>
            <div className="field-row">
              <span className="field-label">Weekly Off</span>
              <span className={`field-value ${!allData.work_order_master.weekly_off && "empty"}`}>
                {allData.work_order_master.weekly_off || "Not specified"}
              </span>
            </div>
            <div className="field-row">
              <span className="field-label">Include W.Off</span>
              <span className="field-value">
                <span className={`boolean-indicator ${allData.work_order_master.include_woff}`}>
                  {allData.work_order_master.include_woff ? "Yes" : "No"}
                </span>
              </span>
            </div>
            <div className="field-row">
              <span className="field-label">Lunch</span>
              <span className="field-value">
                <span className={`boolean-indicator ${allData.work_order_master.lunch}`}>
                  {allData.work_order_master.lunch ? "Yes" : "No"}
                </span>
              </span>
            </div>
            <div className="field-row">
              <span className="field-label">Sector</span>
              <span className={`field-value ${!allData.work_order_master.sector_name && "empty"}`}>
                {allData.work_order_master.sector_name || "Not specified"}
              </span>
            </div>
            <div className="field-row">
              <span className="field-label">Status</span>
              <span className="field-value">
                <span className="status-badge status-open">
                  {allData.work_order_master.wo_open_close || "Open"}
                </span>
              </span>
            </div>
          </div>

          <div className="comments-section">
            <div className="comments-title">Additional information</div>
            <div className="field-row">
              <span className="field-label">Kind Attention</span>
              <span className={`field-value ${!allData.work_order_master.kind_attn && "empty"}`}>
                {allData.work_order_master.kind_attn || "Not specified"}
              </span>
            </div>
            <div className="field-row">
              <span className="field-label">Amendment No</span>
              <span className={`field-value ${!allData.work_order_master.amendment_no && "empty"}`}>
                {allData.work_order_master.amendment_no || "Not specified"}
              </span>
            </div>
            <div className="field-row">
              <span className="field-label">Amendment Date</span>
              <span className={`field-value ${!allData.work_order_master.amendment_date && "empty"}`}>
                {allData.work_order_master.amendment_date
                  ? new Date(allData.work_order_master.amendment_date).toLocaleDateString()
                  : "Not specified"}
              </span>
            </div>
            <div className="field-row">
              <span className="field-label">Demob NIL After</span>
              <span className={`field-value ${!allData.work_order_master.demob_nil_after && "empty"}`}>
                {allData.work_order_master.demob_nil_after
                  ? `${allData.work_order_master.demob_nil_after} days`
                  : "Not specified"}
              </span>
            </div>
            <div className="field-row">
              <span className="field-label">Remarks</span>
              <span className={`field-value ${!allData.work_order_master.remarks && "empty"}`}>
                {allData.work_order_master.remarks || "No remarks provided"}
              </span>
            </div>
          </div>
        </div>

        {/* Work Details Table */}
        <div className="table-wrapper">
          <table className="details-table">
            <thead>
              <tr>
                <th>SR.</th>
                <th>ASSET NAME</th>
                <th>START DATE</th>
                <th>END DATE</th>
                <th>RATE/HR</th>
                <th>DAILY RATE</th>
                <th>MOB</th>
                <th>DEMOB</th>
                <th>TAX TYPE</th>
                <th>TAX %</th>
                <th>TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {validTransactions.map((asset, index) => {
                return (
                  <tr key={index}>
                    <td>{asset.wo_sr_no}</td>
                    <td className="text-left">{asset.asset_name}</td>
                    <td>{new Date(asset.start_date).toLocaleDateString()}</td>
                    <td>{new Date(asset.end_date).toLocaleDateString()}</td>
                    <td className="text-right">₹{formatCurrency(asset.rate_per_hr)}</td>
                    <td className="text-right">₹{formatCurrency(asset.daily_rate)}</td>
                    <td className="text-right">₹{formatCurrency(asset.mob)}</td>
                    <td className="text-right">₹{formatCurrency(asset.demob)}</td>
                    <td className="text-center">{asset.tax_type || "N/A"}</td>
                    <td className="text-center">{asset.tax_percentage || 0}%</td>
                    <td className="text-right">₹{formatCurrency(asset.total_amount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Financial Summary Section */}
        <div className="financial-summary-section">
          <div className="summary-content">
            <div className="summary-note">
              <strong>Note:</strong> All amounts are in Indian Rupees (₹). The total shown is the sum of all transaction amounts (each transaction amount already includes base amount, mobilization, demobilization, and applicable taxes).
            </div>
            <div className="summary-table-wrapper">
              <table className="summary-table">
                <tbody>
                  <tr className="summary-total">
                    <td className="summary-label"><strong>Grand Total:</strong></td>
                    <td className="summary-value"><strong>₹{formatCurrency(grandTotal)}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WorkOrderView;